import type { EligibilityRules, NationalCodeRule } from "@/types/doctor";

export type EligibilityPatient = {
  gender?: string | null;
  birthdate?: string | null;
  username?: string | null;
};

function calculateAge(birthdate?: string | null): number | null {
  if (!birthdate) return null;
  const dob = new Date(birthdate);
  if (isNaN(dob.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age;
}

// Compares the leading (or trailing) substring of nationalCode - the same
// length as the longer of from/to, zero-padded - against [from, to] as
// integers. from === to degenerates to an exact prefix/suffix match. Both
// bounds empty means "no constraint" (always matches). Mirrors ravanhealth's
// AppointmentsController::matchesNationalCodeRange().
function matchesNationalCodeRange(nationalCode: string, from: string | undefined, to: string | undefined, leading: boolean): boolean {
  const fromTrimmed = (from ?? "").trim();
  const toTrimmed = (to ?? "").trim();
  if (fromTrimmed === "" && toTrimmed === "") return true;

  const len = Math.max(fromTrimmed.length, toTrimmed.length);
  if (nationalCode.length < len) return false;

  const segment = leading ? nationalCode.slice(0, len) : nationalCode.slice(-len);
  const value = parseInt(segment, 10) || 0;
  const fromValue = fromTrimmed !== "" ? parseInt(fromTrimmed.padStart(len, "0"), 10) : null;
  const toValue = toTrimmed !== "" ? parseInt(toTrimmed.padStart(len, "0"), 10) : null;

  if (fromValue !== null && value < fromValue) return false;
  if (toValue !== null && value > toValue) return false;
  return true;
}

function matchesAnyNationalCodeRule(nationalCode: string, rules: NationalCodeRule[]): boolean {
  return rules.some(
    (rule) =>
      matchesNationalCodeRange(nationalCode, rule.start_from, rule.start_to, true) &&
      matchesNationalCodeRange(nationalCode, rule.end_from, rule.end_to, false),
  );
}

/**
 * Checks a patient against a trappist's clinic-level eligibility rules.
 * Empty/missing rules are open to everyone. Mirrors ravanhealth's
 * AppointmentsController::meetsEligibilityRules() (age/gender/national-code
 * subset only - rawej has no per-slot settings.eligibility to check, only
 * the doctor's own clinic-wide rules, see settings.appointments.eligibility).
 */
export function matchesEligibilityRules(rules: EligibilityRules | null | undefined, patient: EligibilityPatient): boolean {
  if (!rules) return true;

  if (rules.min_age || rules.max_age) {
    const age = calculateAge(patient.birthdate);
    if (age === null) return false;
    if (rules.min_age && age < rules.min_age) return false;
    if (rules.max_age && age > rules.max_age) return false;
  }

  if (rules.gender && patient.gender !== rules.gender) return false;

  if (rules.national_code_rules && rules.national_code_rules.length > 0) {
    const nationalCode = patient.username ?? "";
    if (!matchesAnyNationalCodeRule(nationalCode, rules.national_code_rules)) return false;
  }

  return true;
}
